/**
 * Scout › Búsquedas — lista + alta/edición/borrado de búsquedas, abrir/cerrar,
 * copiar link. Ver docs/scout-plan/04.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteScoutSearch,
  fetchScoutRoles,
  fetchScoutSearches,
  patchScoutSearch,
  postScoutSearch,
  type ScoutSearchWithCount,
} from '../../api/admin/scout.js';
import { fetchAdminPersonas } from '../../api/admin/personas.js';
import { AdminApiError } from '../../api/admin/_core.js';

/**
 * Base pública de la app Scout para construir el link del candidato. Editable
 * en runtime (localStorage) para poder compartir desde otro dispositivo (p. ej.
 * una URL de Tailscale / túnel) sin rebuildear. Precedencia:
 * localStorage > VITE_SCOUT_BASE_URL (build) > localhost:9004.
 */
const LINK_BASE_STORAGE_KEY = 'scout.linkBase';

/** Opciones básicas para el combo (hardcodeadas). */
const LINK_BASE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'http://localhost:9004', label: 'Local · localhost:9004' },
  {
    value: 'https://nico-laptop-thinkpad.taile9a79b.ts.net:8443',
    label: 'Tailscale (celu) · nico-laptop-thinkpad…ts.net:8443',
  },
  { value: 'https://staging-scout.orborix.com', label: 'Staging · staging-scout.orborix.com' },
];

const DEFAULT_LINK_BASE =
  (import.meta.env as Record<string, string | undefined>)['VITE_SCOUT_BASE_URL'] ??
  LINK_BASE_OPTIONS[0]!.value;

function readLinkBase(): string {
  if (typeof window === 'undefined') return DEFAULT_LINK_BASE;
  return window.localStorage.getItem(LINK_BASE_STORAGE_KEY) || DEFAULT_LINK_BASE;
}

function candidateLink(base: string, token: string): string {
  return `${base.replace(/\/+$/, '')}/?b=${token}`;
}

export function ScoutSearchesPage(): ReactElement {
  const query = useQuery({ queryKey: ['admin', 'scout', 'searches'], queryFn: fetchScoutSearches });
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ScoutSearchWithCount | null>(null);
  const [linkBase, setLinkBase] = useState<string>(readLinkBase);

  const updateLinkBase = (next: string): void => {
    setLinkBase(next);
    const trimmed = next.trim();
    if (trimmed) window.localStorage.setItem(LINK_BASE_STORAGE_KEY, trimmed);
    else window.localStorage.removeItem(LINK_BASE_STORAGE_KEY);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Scout · Búsquedas</h1>
          <p className="page-sub">Entrevistas por voz · un link por búsqueda</p>
        </div>
        <div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditing(null);
              setShowCreate((v) => !v);
            }}
          >
            {showCreate ? 'Cancelar' : 'Nueva búsqueda'}
          </button>
        </div>
      </div>

      <section className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <label style={{ ...fieldStyle, gap: 6 }}>
          Base del link del candidato
          <select className="input" value={linkBase} onChange={(e) => updateLinkBase(e.target.value)} style={{ maxWidth: 520 }}>
            {LINK_BASE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {/* Valor guardado que no está entre los presets (ej. seteado a mano antes). */}
            {!LINK_BASE_OPTIONS.some((o) => o.value === linkBase) && (
              <option value={linkBase}>{linkBase}</option>
            )}
          </select>
          <span style={{ fontSize: 11.5, textTransform: 'none', letterSpacing: 0, color: 'var(--text-tertiary)' }}>
            Define con qué dominio se arma el link que copiás. Elegí Tailscale para probar desde el
            celu. Se guarda en este navegador.
          </span>
        </label>
      </section>

      {showCreate && <SearchForm onDone={() => setShowCreate(false)} />}
      {editing && (
        <SearchForm existing={editing} onDone={() => setEditing(null)} />
      )}

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Búsqueda</th>
                <th>Rol</th>
                <th>Estado</th>
                <th className="r">Candidatos</th>
                <th>Link</th>
                <th className="r">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {query.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    Sin búsquedas todavía. Creá la primera.
                  </td>
                </tr>
              ) : (
                query.data.map((s) => (
                  <SearchRow
                    key={s.id}
                    search={s}
                    linkBase={linkBase}
                    onEdit={() => {
                      setShowCreate(false);
                      setEditing(s);
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function SearchRow({
  search,
  linkBase,
  onEdit,
}: {
  search: ScoutSearchWithCount;
  linkBase: string;
  onEdit: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'scout', 'searches'] });
  };
  const toggle = useMutation({
    mutationFn: () =>
      patchScoutSearch(search.id, { status: search.status === 'open' ? 'closed' : 'open' }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => deleteScoutSearch(search.id),
    onSuccess: invalidate,
  });

  const link = candidateLink(linkBase, search.publicToken);
  const copy = (): void => {
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const confirmDelete = (): void => {
    const msg =
      search.candidateCount > 0
        ? `Eliminar "${search.title}"? Se borran también sus ${search.candidateCount} candidato(s), CVs y resultados. Esto no se puede deshacer.`
        : `Eliminar "${search.title}"? Esto no se puede deshacer.`;
    if (window.confirm(msg)) remove.mutate();
  };

  return (
    <tr>
      <td>
        <Link to={`/scout/${search.id}`}>{search.title}</Link>
      </td>
      <td>
        <code>{search.roleSlug}</code>
      </td>
      <td>
        <span className={search.status === 'open' ? 'pill pill-good' : 'pill'}>{search.status}</span>
      </td>
      <td className="r">{search.candidateCount}</td>
      <td>
        <button type="button" className="btn ghost" onClick={copy} title={link}>
          {copied ? '¡Copiado!' : 'Copiar link'}
        </button>
      </td>
      <td className="r">
        <Link to={`/scout/${search.id}`} className="btn ghost">
          Ver candidatos
        </Link>
        <button type="button" className="btn ghost" onClick={onEdit}>
          Editar
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
        >
          {search.status === 'open' ? 'Cerrar' : 'Reabrir'}
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={confirmDelete}
          disabled={remove.isPending}
        >
          {remove.isPending ? 'Eliminando…' : 'Eliminar'}
        </button>
      </td>
    </tr>
  );
}

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-tertiary)',
};

/** ISO (UTC) → valor para `<input type="datetime-local">` (hora local). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function SearchForm({
  existing,
  onDone,
}: {
  existing?: ScoutSearchWithCount;
  onDone: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const isEdit = existing !== undefined;
  const rolesQuery = useQuery({ queryKey: ['admin', 'scout', 'roles'], queryFn: fetchScoutRoles });
  const personasQuery = useQuery({ queryKey: ['admin', 'personas'], queryFn: fetchAdminPersonas });
  const [title, setTitle] = useState(existing?.title ?? '');
  const [roleSlug, setRoleSlug] = useState(existing?.roleSlug ?? '');
  const [voicePersonaSlug, setVoicePersonaSlug] = useState(existing?.voicePersonaSlug ?? '');
  const [interviewerName, setInterviewerName] = useState(existing?.interviewerName ?? '');
  const [expiresAt, setExpiresAt] = useState(isoToLocalInput(existing?.expiresAt ?? null));

  const mut = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return patchScoutSearch(existing.id, {
          title: title.trim(),
          roleSlug,
          voicePersonaSlug: voicePersonaSlug || null,
          interviewerName: interviewerName.trim() || null,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        });
      }
      return postScoutSearch({
        title: title.trim(),
        roleSlug,
        ...(voicePersonaSlug ? { voicePersonaSlug } : {}),
        ...(interviewerName.trim() ? { interviewerName: interviewerName.trim() } : {}),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'scout', 'searches'] });
      onDone();
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!title.trim() || !roleSlug) return;
    mut.mutate();
  };

  return (
    <section className="card" style={{ padding: 'var(--s-6, 20px)', marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>{isEdit ? `Editar "${existing.title}"` : 'Nueva búsqueda'}</h3>
      <form
        onSubmit={submit}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}
      >
        <label style={fieldStyle}>
          Título
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Líder Técnico — Q2 2026" />
        </label>
        <label style={fieldStyle}>
          Rol (guía .md)
          <select className="input" value={roleSlug} onChange={(e) => setRoleSlug(e.target.value)} required>
            <option value="">— Elegí un rol —</option>
            {(rolesQuery.data ?? []).map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.title} ({r.slug})
              </option>
            ))}
          </select>
        </label>
        <label style={fieldStyle}>
          Voz (opcional)
          <select className="input" value={voicePersonaSlug} onChange={(e) => setVoicePersonaSlug(e.target.value)}>
            <option value="">— Default del módulo / rol —</option>
            {(personasQuery.data ?? []).map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
                {p.accent ? ` · ${p.accent}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label style={fieldStyle}>
          Nombre que muestra (opcional)
          <input
            className="input"
            value={interviewerName}
            onChange={(e) => setInterviewerName(e.target.value)}
            placeholder="Orborix"
          />
          <span style={{ fontSize: 11, textTransform: 'none', letterSpacing: 0, color: 'var(--text-tertiary)' }}>
            Con qué nombre se presenta la entrevistadora. Vacío = usa el nombre de la voz.
          </span>
        </label>
        <label style={fieldStyle}>
          Expira (opcional)
          <input className="input" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button type="submit" className="btn-primary" disabled={mut.isPending}>
            {mut.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear búsqueda'}
          </button>
          <button type="button" className="btn ghost" onClick={onDone} disabled={mut.isPending}>
            Cancelar
          </button>
        </div>
        {mut.isError && (
          <p style={{ gridColumn: '1 / -1', color: 'var(--error)', margin: 0 }}>
            {mut.error instanceof AdminApiError ? mut.error.message : String(mut.error)}
          </p>
        )}
      </form>
    </section>
  );
}
